<template>
  <div class="help-root">

    <div class="help-intro">
      <span class="material-symbols-outlined help-hero-icon">stethoscope</span>
      <p>
        Registra los hallazgos del examen físico realizado durante la consulta:
        documenta observaciones por área de examen, selecciona hallazgos
        patológicos del catálogo y usa el dictado por voz para agilizar el ingreso.
      </p>
    </div>

    <!-- Cómo funciona -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">info</span>
        Cómo funciona
      </h3>
      <div class="help-cards">
        <div class="help-card" v-for="item in flujo" :key="item.titulo">
          <span class="material-symbols-outlined help-card-icon">{{ item.icon }}</span>
          <div>
            <p class="help-card-title">{{ item.titulo }}</p>
            <p class="help-card-desc">{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Campos del formulario -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">edit_note</span>
        Campos del formulario
      </h3>
      <div class="help-cards">
        <div class="help-card" v-for="item in campos" :key="item.titulo">
          <span class="material-symbols-outlined help-card-icon">{{ item.icon }}</span>
          <div>
            <p class="help-card-title">{{ item.titulo }}</p>
            <p class="help-card-desc">{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Hallazgos patológicos -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">fact_check</span>
        Hallazgos patológicos
      </h3>
      <div class="help-cards">
        <div class="help-card" v-for="item in hallazgos" :key="item.titulo">
          <span class="material-symbols-outlined help-card-icon">{{ item.icon }}</span>
          <div>
            <p class="help-card-title">{{ item.titulo }}</p>
            <p class="help-card-desc">{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Dictado por voz -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">mic</span>
        Dictado por voz
      </h3>
      <div class="help-voz">
        <div class="help-voz-item">
          <span class="material-symbols-outlined" style="color:orangered;font-size:28px">mic_off</span>
          <p class="help-card-desc">Micrófono <strong>inactivo</strong>. Pulsa para iniciar el dictado en el campo Observación.</p>
        </div>
        <div class="help-voz-item">
          <span class="material-symbols-outlined" style="color:green;font-size:28px">mic</span>
          <p class="help-card-desc">Micrófono <strong>activo</strong>. El sistema transcribe tu voz en tiempo real. Pulsa para detener.</p>
        </div>
        <div class="help-voz-item">
          <span class="material-symbols-outlined" style="font-size:28px;color:#555">delete_forever</span>
          <p class="help-card-desc"><strong>Reiniciar</strong>. Borra el contenido del campo Observación para comenzar desde cero.</p>
        </div>
      </div>
    </section>

    <!-- Restricción por estado -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">lock</span>
        Restricciones por estado de la atención
      </h3>
      <div class="help-estados" style="margin-bottom:0.5rem">
        <span class="help-estado-badge" style="background:#fff3cd;color:#856404">PENDIENTE</span>
        <span class="help-estado-badge" style="background:#cce5ff;color:#004085">PROCESO</span>
      </div>
      <p class="help-card-desc" style="padding:0 0.25rem">
        La edición del examen físico solo está habilitada cuando la atención
        está en estado <strong>PENDIENTE</strong> o <strong>PROCESO</strong>.
        En cualquier otro estado la columna de edición no se muestra.
      </p>
    </section>

    <div class="help-tip">
      <span class="material-symbols-outlined">lightbulb</span>
      <p>
        Si seleccionas varios hallazgos y pulsas <strong>Registrar</strong>,
        se agregan a la Observación separados por punto y coma sin sobrescribir
        lo existente. Los hallazgos ya presentes no se duplican. Si la
        observación dice <em>Sin patología</em> se reemplaza por completo.
      </p>
    </div>

  </div>
</template>

<script>
export default {
  name: "AyudaExamAten",
  data() {
    return {
      flujo: [
        {
          icon: "table_rows",
          titulo: "Lista de exámenes físicos",
          desc:   "La tabla muestra todas las áreas del examen físico con sus observaciones actuales. El sistema los genera automáticamente al crear la atención.",
        },
        {
          icon: "edit",
          titulo: "Editar un examen",
          desc:   "Pulsa el ícono de edición en la fila del examen que deseas documentar para abrir el formulario con los hallazgos y el campo de observación.",
        },
        {
          icon: "update",
          titulo: "Actualizar",
          desc:   "Guarda los cambios realizados en la observación del examen físico seleccionado.",
        },
      ],
      campos: [
        {
          icon: "stethoscope",
          titulo: "Examen",
          desc:   "Nombre del área o componente del examen físico evaluado (p. ej. cabeza, cuello, tórax, abdomen). Se muestra junto a su ícono representativo y no es editable.",
        },
        {
          icon: "text_snippet",
          titulo: "Observación",
          desc:   "Campo principal de registro clínico. Acepta texto libre, dictado por voz y hallazgos seleccionados del catálogo. Máximo 250 caracteres. Campo obligatorio.",
        },
      ],
      hallazgos: [
        {
          icon: "checklist",
          titulo: "Lista de hallazgos patológicos",
          desc:   "Catálogo de hallazgos predefinidos para el área del examen seleccionada. Se carga automáticamente al abrir el formulario.",
        },
        {
          icon: "touch_app",
          titulo: "Selección múltiple",
          desc:   "Mantén presionada la tecla Ctrl (Windows) o Cmd (Mac) para seleccionar varios hallazgos al mismo tiempo antes de registrarlos.",
        },
        {
          icon: "playlist_add",
          titulo: "Botón Registrar",
          desc:   "Agrega los hallazgos seleccionados al campo Observación separados por punto y coma. No sobrescribe lo ya escrito ni duplica hallazgos existentes.",
        },
      ],
    };
  },
};
</script>

<style src="@/assets/css/ayuda.css" scoped></style>